export const MAX_REPORTING_RESULT_BYTES = 2 * 1024 * 1024

/** Counts UTF-8 bytes without allocating another encoded response buffer. */
const byteLength = (value: string): number => {
  let bytes = 0
  for (let index = 0; index < value.length; index += 1) {
    const code = value.charCodeAt(index)
    if (code <= 0x7f) {
      bytes += 1
    } else if (code <= 0x7ff) {
      bytes += 2
    } else if (
      code >= 0xd800 &&
      code <= 0xdbff &&
      index + 1 < value.length &&
      value.charCodeAt(index + 1) >= 0xdc00 &&
      value.charCodeAt(index + 1) <= 0xdfff
    ) {
      bytes += 4
      index += 1
    } else {
      bytes += 3
    }
  }
  return bytes
}

/**
 * Counts canonical JSON incrementally and stops as soon as the aggregate
 * result exceeds its public 2 MiB boundary. This deliberately avoids creating
 * another full chart-and-dataset string alongside the parsed response.
 */
const canonicalJsonByteLength = (value: unknown, maximum: number): number => {
  let bytes = 0
  const add = (token: string): void => {
    bytes += byteLength(token)
    if (bytes > maximum) {
      throw new Error(
        'reporting snapshot exceeds the 2 MiB canonical result bound',
      )
    }
  }
  const visit = (current: unknown): void => {
    if (Array.isArray(current)) {
      add('[')
      current.forEach((item, index) => {
        if (index > 0) add(',')
        visit(item)
      })
      add(']')
      return
    }
    if (current !== null && typeof current === 'object') {
      add('{')
      Object.keys(current as Record<string, unknown>)
        .sort()
        .forEach((key, index) => {
          if (index > 0) add(',')
          add(JSON.stringify(key))
          add(':')
          visit((current as Record<string, unknown>)[key])
        })
      add('}')
      return
    }
    const encoded = JSON.stringify(current)
    if (encoded === undefined) {
      throw new Error('reporting snapshot contains an unsupported value')
    }
    add(encoded)
  }
  visit(value)
  return bytes
}

export const assertReportingSnapshotSize = (
  chartSpecValue: unknown,
  datasetValue: unknown,
): void => {
  canonicalJsonByteLength(
    { chart_spec: chartSpecValue, dataset: datasetValue },
    MAX_REPORTING_RESULT_BYTES,
  )
}

export const assertReportingDatasetRowsSize = (rows: unknown): void => {
  try {
    canonicalJsonByteLength(rows, MAX_REPORTING_RESULT_BYTES)
  } catch (error) {
    if (error instanceof Error && /2 MiB/.test(error.message)) {
      throw new Error('encoded rows exceed 2 MiB')
    }
    throw error
  }
}

/** Returns the canonical UTF-8 row bytes used by the governed dataset wire contract. */
export const reportingDatasetRowsByteLength = (rows: unknown): number => {
  try {
    return canonicalJsonByteLength(rows, MAX_REPORTING_RESULT_BYTES)
  } catch (error) {
    if (error instanceof Error && /2 MiB/.test(error.message)) {
      throw new Error('encoded rows exceed 2 MiB')
    }
    throw error
  }
}
